import type { Picture, Ring } from "./content";

/** Even-odd rule, as the SVG fills them. */
function contains(ring: Ring, x: number, y: number): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i], [xj, yj] = ring[j];
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

function area(ring: Ring): number {
  let s = 0;
  for (let i = 0; i < ring.length; i++) {
    const [ax, ay] = ring[i], [bx, by] = ring[(i + 1) % ring.length];
    s += ax * by - bx * ay;
  }
  return Math.abs(s) / 2;
}

function distanceToSegment(px: number, py: number, [ax, ay]: [number, number], [bx, by]: [number, number]) {
  const dx = bx - ax, dy = by - ay;
  const t = dx || dy ? Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / (dx * dx + dy * dy))) : 0;
  return Math.hypot(px - (ax + t * dx), py - (ay + t * dy));
}

/**
 * The item a tap lands on, as in the apps: when several outlines hold it the
 * smallest wins (a mug on a table is the mug); when none does, the nearest
 * outline within `tolerance` picture pixels, since fingers are blunt.
 */
export function itemAt(picture: Picture, x: number, y: number, tolerance: number): string | null {
  let best: { id: string; area: number } | null = null;
  for (const [id, rings] of picture.outlines) {
    for (const ring of rings) {
      if (ring.length >= 3 && contains(ring, x, y)) {
        const a = area(ring);
        if (!best || a < best.area) best = { id, area: a };
      }
    }
  }
  if (best) return best.id;
  let nearest: { id: string; d: number } | null = null;
  for (const [id, rings] of picture.outlines) {
    for (const ring of rings) {
      for (let i = 0; i < ring.length; i++) {
        const d = distanceToSegment(x, y, ring[i], ring[(i + 1) % ring.length]);
        if (d <= tolerance && (!nearest || d < nearest.d)) nearest = { id, d };
      }
    }
  }
  return nearest?.id ?? null;
}

export const path = (rings: Ring[]) =>
  rings.filter((r) => r.length >= 3).map((r) => "M" + r.map(([x, y]) => `${x},${y}`).join("L") + "Z").join("");
